import React, { useState } from "react";
import { XIcon, CheckIcon, XCircleIcon, RefreshIcon } from "@heroicons/react/outline";
import { ExclamationIcon } from "@heroicons/react/solid";

const QuickActionModal = ({
  isOpen,
  onClose,
  applications = [],
  onApprove,
  onDeny,
  onCancelAndRefund,
  isLoading = false,
}) => {
  const [selectedApp, setSelectedApp] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  if (!isOpen) return null;

  const pendingApps = applications.filter(
    (app) => app.status?.toLowerCase() === "applied"
  );
  const registeredApps = applications.filter(
    (app) => app.status?.toLowerCase() === "registered" ||
             app.status?.toLowerCase() === "approved"
  );

  const handleApprove = async (app) => {
    setProcessingId(app.id);
    try {
      await onApprove(app.id);
      setConfirmAction(null);
      setSelectedApp(null);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeny = async (app) => {
    setProcessingId(app.id);
    try {
      await onDeny(app.id);
      setConfirmAction(null);
      setSelectedApp(null);
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelRefund = async (app) => {
    setProcessingId(app.id);
    try {
      await onCancelAndRefund(app.id, app);
      setConfirmAction(null);
      setSelectedApp(null);
    } finally {
      setProcessingId(null);
    }
  };

  const ConfirmationDialog = ({ app, action }) => {
    const isCancel = action === "cancel";
    const isDeny = action === "deny";
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
          <div className="flex items-center justify-center mb-4">
            <div className={`p-3 rounded-full ${isCancel || isDeny ? 'bg-red-100' : 'bg-green-100'}`}>
              <ExclamationIcon className={`h-8 w-8 ${isCancel || isDeny ? 'text-red-600' : 'text-green-600'}`} />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-center text-grey-900 mb-2">
            {isCancel ? "Cancel Registration & Refund?" : 
             isDeny ? "Deny this Application?" :
             "Approve this Application?"}
          </h3>
          <p className="text-sm text-grey-600 text-center mb-6">
            {isCancel ? (
              <>
                This will cancel <strong>{app.company}</strong>'s registration and process a full refund. 
                This action cannot be undone.
              </>
            ) : isDeny ? (
              <>
                <strong>{app.company}</strong> will be notified that their application has been declined.
              </>
            ) : (
              <>
                <strong>{app.company}</strong> will be approved and notified to complete payment.
              </>
            )}
          </p>
          
          {isCancel && (app.payment?.toLowerCase() === "paid" || app.payment?.toLowerCase() === "completed") && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-amber-800">
                <strong>Refund request will be submitted:</strong> The vendor's payment will be marked for refund and processed within 5-10 business days.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => {
                setConfirmAction(null);
                setSelectedApp(null);
              }}
              disabled={processingId === app.id}
              className="flex-1 py-2.5 px-4 border border-grey-300 rounded-lg text-sm font-medium text-grey-700 hover:bg-grey-50 transition-colors disabled:opacity-50"
            >
              Go Back
            </button>
            <button
              onClick={() => {
                if (isCancel) handleCancelRefund(app);
                else if (isDeny) handleDeny(app);
                else handleApprove(app);
              }}
              disabled={processingId === app.id}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                isCancel || isDeny ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {processingId === app.id ? (
                <>
                  <RefreshIcon className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {isCancel ? "Yes, Cancel & Refund" : 
                   isDeny ? "Yes, Deny" : 
                   "Yes, Approve"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ApplicationCard = ({ app, showActions = true, isRegistered = false }) => (
    <div className="border border-grey-200 rounded-lg p-4 hover:border-grey-300 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-grey-900 truncate">{app.company}</h4>
          <p className="text-xs text-grey-500 mt-0.5">{app.email}</p>
          {app.phone && <p className="text-xs text-grey-400">{app.phone}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            app.status?.toLowerCase() === 'applied' ? 'bg-amber-100 text-amber-700' :
            app.status?.toLowerCase() === 'registered' ? 'bg-green-100 text-green-700' :
            app.status?.toLowerCase() === 'approved' ? 'bg-blue-100 text-blue-700' :
            'bg-grey-100 text-grey-700'
          }`}>
            {app.status}
          </span>
          {app.payment && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              app.payment?.toLowerCase() === 'paid' ? 'bg-green-100 text-green-700' :
              'bg-grey-100 text-grey-600'
            }`}>
              {app.payment}
            </span>
          )}
        </div>
      </div>
      
      {showActions && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-grey-100">
          {!isRegistered ? (
            <>
              <button
                onClick={() => {
                  setSelectedApp(app);
                  setConfirmAction("approve");
                }}
                disabled={processingId === app.id}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                <CheckIcon className="h-3.5 w-3.5" />
                Approve
              </button>
              <button
                onClick={() => {
                  setSelectedApp(app);
                  setConfirmAction("deny");
                }}
                disabled={processingId === app.id}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                <XCircleIcon className="h-3.5 w-3.5" />
                Deny
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                setSelectedApp(app);
                setConfirmAction("cancel");
              }}
              disabled={processingId === app.id}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 border border-red-300 text-red-600 hover:bg-red-50 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              <XCircleIcon className="h-3.5 w-3.5" />
              Cancel & Refund
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col mx-4 shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-grey-200">
            <div>
              <h2 className="text-lg font-semibold text-grey-900">Quick Actions</h2>
              <p className="text-sm text-grey-500">Manage vendor applications</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-grey-100 rounded-lg transition-colors"
            >
              <XIcon className="h-5 w-5 text-grey-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {pendingApps.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-2 w-2 bg-amber-500 rounded-full animate-pulse"></div>
                  <h3 className="text-sm font-semibold text-grey-900">
                    New Applications ({pendingApps.length})
                  </h3>
                </div>
                <div className="grid gap-3">
                  {pendingApps.map((app) => (
                    <ApplicationCard key={app.id} app={app} />
                  ))}
                </div>
              </div>
            )}

            {pendingApps.length === 0 && (
              <div className="text-center py-8 mb-6">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                  <CheckIcon className="h-6 w-6 text-green-600" />
                </div>
                <p className="text-sm font-medium text-grey-900">All caught up!</p>
                <p className="text-xs text-grey-500">No pending applications to review</p>
              </div>
            )}

            {registeredApps.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-grey-900 mb-3">
                  Registered Vendors ({registeredApps.length})
                </h3>
                <div className="grid gap-3">
                  {registeredApps.slice(0, 5).map((app) => (
                    <ApplicationCard key={app.id} app={app} isRegistered={true} />
                  ))}
                  {registeredApps.length > 5 && (
                    <p className="text-xs text-grey-500 text-center py-2">
                      + {registeredApps.length - 5} more registered vendors
                    </p>
                  )}
                </div>
              </div>
            )}

            {applications.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm text-grey-500">No applications yet</p>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-grey-200 bg-grey-50">
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-grey-700 hover:bg-grey-800 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {confirmAction && selectedApp && (
        <ConfirmationDialog app={selectedApp} action={confirmAction} />
      )}
    </>
  );
};

export default QuickActionModal;
